import React, { useState, createRef, useContext, useEffect, useRef, lazy, Suspense } from "react";
import { createNoteText, formatNoteName } from "../../utils/FormatNoteNameAndText";
import { PDF_FORMS } from "../../utils/constants/Notes";
import { getFullName } from "../../services/Authentication";
import { NoteDisplayCard } from "./DisplayText";
import { AlertContext } from "../../context/AlertFlag";
import { Box, Typography, Button, Select, Option, Divider, IconButton, CircularProgress} from "@mui/joy";
import { NotepadText, Share, X, Check, Pencil, Trash  } from "lucide-react";
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { copyToClipboard } from "../../utils/Clipboard";
import ConfirmDeleteNoteModal from "../modals/ConfirmDeleteGenNote";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { get_generate_note } from "../../services/GenerateNote";
import { downloadTextoTxt } from "../../utils/DownloadToTxt";
import NoteToPDF from "../../utils/DownloadNote";
import { handleErrorSentry } from "../../utils/constants/Error";

const LazyPDFDownloadLink = lazy(() => import('@react-pdf/renderer').then(mod => ({ default: mod.PDFDownloadLink })));
const LazyDocEmbedder = lazy(() => import('../embed-pdf/buildpdf'));

// Copy full text, save text
export function NoteTextComponent({ visit, note, lift_editing=()=>{}, lift_save=()=>{}, lift_delete=()=>{} }) {
    const { addAlert } = useContext(AlertContext);
    const [editingNote, setEditingNoteFlag] = useState(false);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [showPdfExport, setShowPdfExport] = useState(false);

    const queryClient = useQueryClient();
    const mutation_check_note = useMutation({
        mutationFn: get_generate_note,
        onSuccess: (data) => {
            if (data.generated_result && data.generated_result.value) {
                queryClient.invalidateQueries({ queryKey: ['visit', visit.id] })
                if (checkNoteIntervalRef.current) {
                    clearInterval(checkNoteIntervalRef.current);
                    checkNoteIntervalRef.current = null;
                }
            }
        },
        onError: (error) => {
            handleErrorSentry(error, 'onerror');
        }
    })

    const checkNoteIntervalRef = useRef(null);

    const titles = useRef([]);
    const noteParts = useRef([]);
    const noteBodyReferences = useRef([]);


    const copyNote = () => {
        copyToClipboard(createNoteText(titles.current, noteBodyReferences.current));
        addAlert("Copied note!", "success");
    }

    const chooseText = () => {
        try {
            if (!note.user_edited_result.value || note.user_edited_result.value.length <= 0) {
                return note.generated_result.value;
            } else {
                return note.user_edited_result.value;
            }
        } catch (error) {
            addAlert("Something went wrong", "danger");
        }
    };

    const setEditingNote = (value) => {
        setEditingNoteFlag(value);
        lift_editing(value);
    };

    const handleCancelSaveNote = () => {
        for (let index = 0; index < noteBodyReferences.current.length; index++) {
            if (noteBodyReferences.current[index].current) {
                noteBodyReferences.current[index].current.value = noteParts.current[index];
            }
        }
    };

    const handleSaveNote = async () => {
        await lift_save(createNoteText(titles.current, noteBodyReferences.current), note.id);
    };

    const handleDeleteNote = async () => {
        await lift_delete(note.id)
    }

    const triggerExport = (setting) => {
        try {
            switch (setting) {
                case "txt":
                    downloadTextoTxt(chooseText(), visit.title, note.created_at);
                    break;
                case "pdf":
                    break;
            }
        } catch (error) {
            console.log(error);
        }
    };

    const setRefs = (index) => {
        noteBodyReferences.current[index] = createRef();
    };

    useEffect(() => {
        if (!note.generated_result || !("value" in note.generated_result)) {
            checkNoteIntervalRef.current = setInterval(() => {
                mutation_check_note.mutate({ visit_id: visit.id, note_id: note.id })
            }, 2000)
        }

        return () => {
            if (checkNoteIntervalRef.current) {
                clearInterval(checkNoteIntervalRef.current)
            }
        }
    }, [note])

    return (
        <div key={note.id}>
            {note && note.generated_result && "value" in note.generated_result ? 
            <>
                {PDF_FORMS.includes(note.note_type) ? 
                    <Box sx={{ px: 2 }}>
                        <Suspense fallback={ <div> Loading... </div> }>
                            <LazyDocEmbedder note={note}/>
                        </Suspense>
                    </Box>
                    :
                    <Box>
                    {openConfirmDelete && <ConfirmDeleteNoteModal open={openConfirmDelete} on_close={() => { setOpenConfirmDelete(false) }} delete_note={handleDeleteNote}/>}
                    <Box sx={{ display: "flex", flexDirection: {xs: "column", sm: "row"}, justifyContent: "space-between", flexWrap: "wrap", px: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <NotepadText
                                style={{
                                    color: "var(--main-blue)",
                                    height: "2.75em",
                                    width: "2.75em",
                                    paddingRight: 2,
                                }}
                            />
                            <Typography level="title-lg" sx={{ pb: 0.4 }}>
                                {formatNoteName(note.note_type)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: {xs: "column", sm: "row"}, gap: 2 }}>
                            <Button sx={{ display: "flex", whiteSpace: "nowrap", backgroundColor: "var(--main-blue)" }} startDecorator={<CopyAllIcon />} onClick={() => copyNote()}>
                                Copy Note
                            </Button>
                            {editingNote && (
                                <Button
                                    startDecorator={<X />}
                                    sx={{ backgroundColor: "white", color: "gray", display: "flex" }}
                                    variant="plain"
                                    onClick={() => {
                                        handleCancelSaveNote();
                                        setEditingNote(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                sx={{ backgroundColor: "white", display: "flex" }}
                                variant="plain"
                                startDecorator={editingNote ? <Check /> : <Pencil />}
                                onClick={async () => {
                                    if (editingNote) {
                                        await handleSaveNote();
                                        setEditingNote(false);
                                    } else {
                                        setEditingNote(true);
                                    }
                                }}
                            >
                                {editingNote ? "Save" : "Edit"}
                            </Button>
                            <Select
                                value="export"
                                variant="plain"
                                sx={{ minWidth: 95, py: 2, color: "var(--dark-blue-button)", alignSelf: "center" }}
                                onListboxOpenChange={() => {
                                    setShowPdfExport(true);
                                }}
                                onClose={() => {
                                    setShowPdfExport(false);
                                }}
                                onChange={(event, value) => {
                                    triggerExport(value);
                                }}
                                indicator={null}
                                startDecorator={<Share style={{ height: "20px", width: "20px" }} />}
                            >
                                <Option value="export" disabled sx={{ display: "none" }}>
                                    Export
                                </Option>
                                <Option value="txt">to TXT file</Option>
                                {showPdfExport && <Option value="pdf">
                                    <div>
                                        <Suspense fallback={<div>Loading PDF tools...</div>}>
                                            <LazyPDFDownloadLink
                                                document={
                                                    <NoteToPDF
                                                        note={note}
                                                        createdBy={getFullName()}
                                                        noteType={note.note_type}
                                                    />
                                                }
                                                fileName={visit.title}
                                            >
                                                to PDF file
                                            </LazyPDFDownloadLink>
                                        </Suspense>
                                    </div>
                                </Option>}
                            </Select>
                            <IconButton color="danger" onClick={() => { setOpenConfirmDelete(true) }}>
                                <Trash style={{ width: "20px" }}/>
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ whiteSpace: "pre-wrap", py: 1, px: 3 }}>
                        <NoteDisplayCard 
                        note={chooseText()} 
                        liftTitles={(new_titles) => { titles.current = new_titles }}
                        createRefAtIndex={setRefs}
                        assignedRefs={noteBodyReferences.current}
                        parentEditable={editingNote}
                        liftParts={(parts) => { noteParts.current = parts }}
                        />
                    </Box>
                </Box>
                }
            </> 
            : 
            <Box sx={{ display: "flex", height: "50vh", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>
                <Typography level='body-lg'>
                    Capturing visit context... Please refresh the page to check the updated status!
                </Typography>
                <CircularProgress size="lg"/>
            </Box>
            }
        </div>
    );
};

// Conditionally rendered depending on state of generated_result, so can expect value
NoteTextComponent.propTypes = ({
    note: PropTypes.shape({
        id: PropTypes.string.isRequired,
        created_at: PropTypes.string.isRequired,
        note_type: PropTypes.string.isRequired,
        user_edited_result: PropTypes.shape({ value: PropTypes.string }),
        generated_result: PropTypes.shape({ value: PropTypes.string }),
    }),
    visit: PropTypes.shape({
        title: PropTypes.string.isRequired,
        generate_notes: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
    lift_editing: PropTypes.func.isRequired,
    lift_save: PropTypes.func.isRequired,
    lift_delete: PropTypes.func.isRequired,
})